import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getSimpleBranchingQuery, pluralize, Team } from '@iotv/datamodel-core';
import { AdjacentType, getObjectHash } from '@iotv/iotv-v3-types';
import { Button, ButtonGroup, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { shortDefs } from '../../ArbitrationObjects/ListControlArbitrator/shortDefs';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { isViewOnly } from '../../helpers/access';
import { ActionIcons } from '../../Iconography';
import { ComponentMode, Severity } from '../../types';
import useDetailCard from '../../v2/components/generic/ObjectView/DetailCard/useDetailCard';
import ObjectCard from '../../v2/components/generic/ObjectView/ObjectCard';
import { DialogWrapper } from '../common/DiaglogWrapper';
import { FullSkeleton } from '../common/FullSkeleton';
import { getViewDefintionFromObject } from '../factories/AttributeDefinitions';
import { useFlexibleList } from '../factories/Listerlizer/useFlexibleList';
import { LinkableTeamPeopleDialog } from './LinkableTeamPeopleDialog';
import { TeamPersonViewDefinition } from './viewDefinition';
const debug = process.env.REACT_APP_DEBUG && false;
const TeamDetailCard = (props) => {
    const { viewObject, applicationData, UserFunctions, TransactionFunctions, roleArbitrationInstance, userSelectedGroup, contextUser, contextCustomer, allowedActions, } = props;
    const team = viewObject.matchedPrimary;
    const [savingPeople, setSavingPeople] = useState(false);
    const [showLinkPeople, setShowLinkPeople] = useState(false);
    const gridApiRef = useGridApiRef();
    const allowedControls = team && roleArbitrationInstance && !isViewOnly(userSelectedGroup)
        ? {
            controls: roleArbitrationInstance.adjacentQueryToControlActions({
                contextObject: team,
                adjacentFilter: { adjacentType: AdjacentType.CHILD, objectType: 'Person' },
            }),
        }
        : { controls: shortDefs.noAction };
    /** Fetch edges to People **/
    const [peopleEdges, setPeopleEdges] = useState(undefined);
    const [peopleRows, setPeopleRows] = useState(undefined);
    const nemHash = getObjectHash(applicationData);
    useEffect(() => {
        if (!team || !applicationData)
            return;
        const peopleEdges = applicationData.getChildEdges(team).filter((e) => e.type === 'Person');
        const peopleRows = peopleEdges.map((e) => ({
            ...e,
            teamRole: e.edgeAttributes?.teamRole,
        }));
        setPeopleEdges(peopleEdges);
        setPeopleRows(peopleRows);
    }, [nemHash]);
    useEffect(() => {
        if (!team)
            return;
        const peopleQuery = getSimpleBranchingQuery('Team', 'Person', AdjacentType.CHILD);
        ConsumerDao.getGraphQueryRequestV2({ query: peopleQuery, contextObjects: [team] }).then((people_res) => {
            console.log(people_res);
            if (people_res.err || !people_res.data) {
                console.error('TeamPeopleList', 'err getting people', people_res.err?.message ?? 'No data');
                return;
            }
            UserFunctions.addObjectsToState(people_res.data.getContainedNodesAndEdges());
        });
    }, [team?.sk]);
    /** Detail Card **/
    const teamMutatedTransactionFns = {
        ...TransactionFunctions,
        saveObject: async (object) => {
            TransactionFunctions.saveObject(object);
            // Update team role edge attributes
            if (peopleEdges && peopleRows) {
                const newPersonEdges = [];
                for (const personEdge of peopleEdges) {
                    const personRow = peopleRows.find((pr) => pr.sk === personEdge.sk);
                    if (!personRow) {
                        continue;
                    }
                    if (personRow.teamRole !== personEdge.edgeAttributes?.teamRole) {
                        const newPersonEdge = {
                            ...personEdge,
                            edgeAttributes: { ...personEdge.edgeAttributes, teamRole: personRow.teamRole },
                        };
                        newPersonEdges.push(newPersonEdge);
                    }
                }
                console.log('Would save new person edges', newPersonEdges);
                setSavingPeople(true);
                ConsumerDao.batchPut({ Items: newPersonEdges }).then((savePeople_res) => {
                    if (savePeople_res.err) {
                        console.error('TeamView', savePeople_res.err);
                    }
                    else {
                        UserFunctions.addObjectsToState(newPersonEdges); // This line is what syncs the detail card and makes the buttons disable again
                    }
                    setSavingPeople(false);
                });
            }
        },
    };
    const { localObject: localTeam, detailCardComponents } = useDetailCard({
        ...props,
        viewDefinition: getViewDefintionFromObject(new Team({})),
        matchedPrimary: team ?? new Team({}), // This actually can't happen but TS won't shut up
        //editMode: editMode === ComponentMode.EDIT ? ComponentMode.EDIT : ComponentMode.VIEW,
        userFunctions: UserFunctions,
        transactionFunctions: teamMutatedTransactionFns,
        showContextSubCard: false,
        resetCallback: () => {
            const peopleRows = setPeopleRows((peopleEdges ?? []).map((e) => ({
                ...e,
                teamRole: e.edgeAttributes?.teamRole,
            })));
        },
        hasBeenEditedFn: (primary, local) => {
            if (getObjectHash(primary) !== getObjectHash(local))
                return true;
            // if any team roles have been updated then there are edits
            if (peopleEdges && peopleRows) {
                for (const personEdge of peopleEdges) {
                    const personRow = peopleRows.find((pr) => pr.sk === personEdge.sk);
                    if (!personRow)
                        return true;
                    if (personRow.teamRole !== personEdge.edgeAttributes?.teamRole)
                        return true;
                }
            }
            return false;
        },
    });
    const [unlinkingPeople, setUnlinkingPeople] = useState(false);
    const unlinkSelectedPeople = () => {
        const selectedPeopleEdges = selectedObjects.map((person) => peopleEdges?.find((pe) => pe.id === person.id)).filter((p) => !!p);
        setUnlinkingPeople(true);
        ConsumerDao.batchDelete({ Items: selectedPeopleEdges }).then((unlink_res) => {
            setUnlinkingPeople(false);
            if (unlink_res.err || !unlink_res.data) {
                console.error('TeamView', 'err in unlink', unlink_res.err?.message ?? 'No data');
                UserFunctions.setUserMessage({
                    id: 'unlinkerr',
                    content: 'There was an error when trying to unlink. Please try again in a moment.',
                    label: 'Unlink Failed',
                    type: 'SnackBar',
                    severity: Severity.error,
                });
            }
            else {
                UserFunctions.setUserMessage({
                    id: 'unlinkerr',
                    content: `Unlinked ${selectedPeopleEdges.length} ${pluralize('person', selectedPeopleEdges.length)} from ${team?.name}`,
                    label: 'Unlink Succeeded',
                    type: 'SnackBar',
                    severity: Severity.success,
                });
                UserFunctions.removeObjectsFromState(selectedPeopleEdges);
            }
        });
    };
    const { component: teamListComponent, selectedObjects } = useFlexibleList({
        listItems: peopleRows ?? [],
        apiRef: gridApiRef,
        editOverride: allowedActions?.edit === true ? ComponentMode.EDIT : ComponentMode.VIEW,
        loading: unlinkingPeople,
        checkboxSelection: true,
        processRowUpdate: (newRow, oldRow) => {
            setPeopleRows((old) => [...(old ?? []).filter((pr) => pr.sk !== newRow.sk), newRow]);
            return newRow;
        },
        disableRowSelectionOnClick: true,
        viewDefinition: TeamPersonViewDefinition,
    });
    return team ? (_jsxs(Stack, { gap: 1, children: [detailCardComponents.wholeComponent, _jsx(Stack, { direction: "row", justifyContent: 'flex-start', children: allowedControls.controls.find && (_jsxs(ButtonGroup, { children: [_jsx(Button, { variant: "contained", size: "small", disabled: selectedObjects.length < 1 || allowedActions?.edit !== true, startIcon: _jsx(ActionIcons.Unlink, {}), onClick: () => unlinkSelectedPeople(), children: "Unlink" }), _jsx(Button, { variant: "contained", size: "small", startIcon: _jsx(ActionIcons.Link, {}), onClick: () => setShowLinkPeople(true), disabled: allowedActions?.edit !== true, children: "Link" })] })) }), peopleRows && teamListComponent, _jsx(DialogWrapper, { header: 'Link Person to Team',
                open: showLinkPeople,
                setClosed: () => setShowLinkPeople(false),
                children: (_jsx(_Fragment, { children: _jsx(LinkableTeamPeopleDialog, { team,
                        contextUser,
                        contextCustomer,
                        userFunctions: UserFunctions,
                        closeDialog: () => setShowLinkPeople(false) }) })) }, `teamPersonLinkDialog`)] })) : (_jsx(FullSkeleton, {}));
};
export const TeamView = (props) => {
    const { viewObject, allowedActions } = props;
    const team = viewObject.matchedPrimary;
    return team ? (_jsx(ObjectCard, { ...props,
        matchedPrimary: team,
        //editMode: initialEditMode,
        allowedActions,
        additionalTabs: {
            Details: TeamDetailCard,
        } })) : (_jsx(FullSkeleton, {}));
};
